import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Field } from '../components/kit';
import { Brand } from '../components/Brand';
import { useAuth } from '../auth/AuthContext';
import { useBranding } from '../branding/BrandingProvider';
import { ApiRequestError } from '../api/client';
import './auth.css';

export function Login() {
  const { login, verify2fa } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgot, setForgot] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 2FA aşaması
  const [stage, setStage] = useState<'login' | '2fa'>('login');
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);

  const goHome = (role: string) => navigate(role === 'bayi' ? '/bayi' : '/panel', { replace: true });

  const onLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res.twofa) setStage('2fa');
      else goHome(res.user.role);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Giriş yapılamadı');
    } finally {
      setBusy(false);
    }
  };

  const on2fa = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await verify2fa(code.trim(), rememberDevice);
      goHome(user.role);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Doğrulama başarısız');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <aside className="auth-aside">
          <Brand variant="auth" />
          <div className="auth-aside-tag">Tahsilat ve ödeme süreçlerinizi kolaylaştıran akıllı çözüm.</div>
          <div className="auth-aside-copy">© 2026 {branding.appName}. Tüm hakları saklıdır.</div>
        </aside>

        <div className="auth-main">
          {stage === 'login' ? (
            <>
              <h1>Giriş yap</h1>
              <p className="auth-sub">Hesabınıza erişmek için giriş yapın.</p>
              <form className="auth-form" onSubmit={onLogin}>
                {error && <div className="auth-error">{error}</div>}
                <Field label="E-posta" id="email" type="email" autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required />
                <Field label="Şifre" id="password" type="password" autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
                <div className="auth-row">
                  <span />
                  <button type="button" className="linklike" onClick={() => setForgot((f) => !f)}>
                    Şifremi unuttum?
                  </button>
                </div>
                {forgot && (
                  <div className="auth-note">
                    Şifrenizi unuttuysanız yöneticinizle iletişime geçin; hesabınızın şifresini sıfırlayabilir.
                  </div>
                )}
                <Button className="auth-submit" type="submit" disabled={busy}>
                  {busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
                </Button>
              </form>
              <div className="auth-foot">
                Hesabınız yok mu? <Link to="/kayit">Bayi kaydı oluşturun</Link>
              </div>
            </>
          ) : (
            <>
              <h1>Doğrulama kodu</h1>
              <p className="auth-sub">
                <strong>{email}</strong> adresine 6 haneli bir kod gönderdik. Girişi tamamlamak için kodu girin.
              </p>
              <form className="auth-form" onSubmit={on2fa}>
                {error && <div className="auth-error">{error}</div>}
                <Field label="Doğrulama kodu" id="code" inputMode="numeric" autoComplete="one-time-code"
                  placeholder="6 haneli kod" value={code} maxLength={6}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
                <label className="auth-remember">
                  <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />
                  Bu cihazı 30 gün hatırla (bir daha kod sorulmaz)
                </label>
                <Button className="auth-submit" type="submit" disabled={busy || code.length < 6}>
                  {busy ? 'Doğrulanıyor…' : 'Doğrula ve giriş yap'}
                </Button>
                <button type="button" className="linklike" style={{ alignSelf: 'center' }}
                  onClick={() => { setStage('login'); setCode(''); setError(null); }}>
                  ← Geri dön
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
